import type { EmailSignature, SignatureImage } from '../../../models/MasterAdmin/marketingEmail';
import { appendSignatureToBody } from '../../../models/MasterAdmin/marketingEmail';

function escapeHtml(value: string): string {
  return String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/"/g, '&quot;');
}

/** Email-safe image block for signature logos (absolute HTTPS URLs). */
export function buildSignatureImagesHtml(
  images: SignatureImage[] = [],
  existingHtml = ''
): string {
  const existing = String(existingHtml || '');
  if (/<img\b/i.test(existing)) return '';

  const toAdd = images.filter((img) => img?.url && !existing.includes(img.url));
  if (!toAdd.length) return '';

  return toAdd
    .map(
      (img) =>
        `<p style="margin:10px 0 0;line-height:0;"><img src="${img.url}" alt="${escapeHtml(img.name || 'Logo')}" width="140" style="max-width:140px;height:auto;display:block;border:0;outline:none;text-decoration:none;-ms-interpolation-mode:bicubic;" /></p>`
    )
    .join('');
}

export function signatureImageHtml(img: SignatureImage): string {
  return `<p style="margin:10px 0 0;line-height:0;"><img src="${img.url}" alt="${escapeHtml(img.name || 'Logo')}" width="140" style="max-width:140px;height:auto;display:block;border:0;outline:none;text-decoration:none;-ms-interpolation-mode:bicubic;" /></p>`;
}

function normalizeSignatureImages(html: string): string {
  return html.replace(/<img\b([^>]*)\/?>/gi, (_match, attrs: string) => {
    const srcMatch = attrs.match(/\bsrc\s*=\s*["']([^"']+)["']/i);
    const altMatch = attrs.match(/\balt\s*=\s*["']([^"']*)["']/i);
    const src = srcMatch?.[1] || '';
    const alt = altMatch?.[1] || 'Logo';
    if (!src) return _match;
    return `<img src="${src}" alt="${escapeHtml(alt)}" width="140" style="max-width:140px;height:auto;display:block;border:0;outline:none;text-decoration:none;-ms-interpolation-mode:bicubic;" />`;
  });
}

export function buildSignatureBlock(
  signature?: EmailSignature | null,
  signatureImages: SignatureImage[] = []
): string {
  let sigHtml = signature?.htmlBody?.trim() || '';
  if (sigHtml) sigHtml = normalizeSignatureImages(sigHtml);
  const imagesHtml = buildSignatureImagesHtml(signatureImages, sigHtml);
  return `${sigHtml}${imagesHtml}`.trim();
}

export function buildFullEmailHtml(
  htmlBody: string,
  options: {
    includeSignature?: boolean;
    signature?: EmailSignature | null;
    signatureImages?: SignatureImage[];
  } = {}
): string {
  const { includeSignature = true, signature, signatureImages = [] } = options;
  if (!includeSignature) return htmlBody || '';
  const sigBlock = buildSignatureBlock(signature, signatureImages);
  if (!sigBlock) return htmlBody || '';
  return appendSignatureToBody(htmlBody || '', sigBlock);
}
